import { supabase } from "../supabaseClient";

/**
 * Fetch ALL pop_plans rows (any status) so the dashboard can build the FY dropdown.
 * Returns: { plans: [...], error }
 */
export const fetchAllPOPPlans = async () => {
  try {
    const { data, error } = await supabase
      .from("pop_plans")
      .select("id, name, start_date, end_date, status")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return { plans: data || [], error: null };
  } catch (err) {
    console.error("❌ fetchAllPOPPlans error:", err);
    return { plans: [], error: err };
  }
};

/**
 * Fetch bookings + slabs for a given list of POP plan IDs.
 * If planIds is null/undefined, falls back to all Active plans (original behaviour).
 * All other logic unchanged.
 */
export const fetchPOPData = async (cpId, planIds = null) => {
  if (!cpId) return { plans: [], error: null };

  try {
    let plans;

    if (planIds && planIds.length > 0) {
      // Fetch only the plans whose IDs are in the supplied list
      const { data, error: planError } = await supabase
        .from("pop_plans")
        .select("id, name, start_date, end_date")
        .in("id", planIds)
        .order("created_at", { ascending: false });

      if (planError) throw planError;
      plans = data;
    } else {
      // Original behaviour: all Active plans
      const { data, error: planError } = await supabase
        .from("pop_plans")
        .select("id, name, start_date, end_date")
        .eq("status", "Active")
        .order("created_at", { ascending: false });

      if (planError) throw planError;
      plans = data;
    }

    if (!plans || plans.length === 0) {
      console.warn("⚠️ No POP plans found");
      return { plans: [], error: null };
    }

    const result = [];

    for (const plan of plans) {
      const planId = plan.id;

      const { data: rows, error: dataError } = await supabase
        .from("CpBookingComputState")
        .select(`
          allotment_payment_id,
          cp_id,
          project_id,
          pop_amount,
          pop_slab_id,
          booking_value,
          created_at,

          Projects:project_id (
            projectId,
            projectName,
            projectShortCode
          )
        `)
        .eq("cp_id", cpId)
        .eq("pop_plan_id", planId);

      if (dataError) throw dataError;

      const { data: payouts, error: payoutError } = await supabase
  .from("CpProjectPayout")
  .select(`
    project_id,
    enablePayout,
    isCpPaymentEntryDone,
    pop_plan_id
  `)
  .eq("cp_id", cpId)
  .eq("pop_plan_id", planId)
  .eq("component_type", "POP"); // ✅ IMPORTANT

  const payoutMap = {};

(payouts || []).forEach((p) => {
  payoutMap[p.project_id] = {
    enablePayout: p.enablePayout,
    isCpPaymentEntryDone: p.isCpPaymentEntryDone,
  };
});

      const { data: slabs, error: slabError } = await supabase
        .from("pop_slabs")
        .select(`
          id,
          from_value,
          to_value,
          percentage,
          amount,
          sort_order
        `)
        .eq("pop_plan_id", planId)
        .order("sort_order", { ascending: true });

      if (slabError) throw slabError;

      const allotmentIds = (rows || []).map((r) => r.allotment_payment_id).filter(Boolean);
      let agreementMap = {};
      if (allotmentIds.length > 0) {
        const { data: agreements } = await supabase
          .from("AgreementForm")
          .select(`allotmentPaymentId, agreementStatus`)
          .in("allotmentPaymentId", allotmentIds);
        (agreements || []).forEach((a) => {
          agreementMap[a.allotmentPaymentId] = a.agreementStatus?.agreement_date || null;
        });
      }

   const enrichedRows = (rows || []).map((row) => ({
  ...row,
  enablePayout: payoutMap[row.project_id]?.enablePayout || false,
  isCpPaymentEntryDone:
    payoutMap[row.project_id]?.isCpPaymentEntryDone || false,
  agreement_date: agreementMap[row.allotment_payment_id] || null,
}));

result.push({
  plan,
  data: enrichedRows, // ✅ USE THIS
  slabs: slabs || [],
});
    }

    return { plans: result, error: null };
  } catch (err) {
    console.error("❌ fetchPOPData error:", err);
    return { plans: [], error: err };
  }
};